import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { Link } from 'react-router-dom';

const MyOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchMyOrders();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchMyOrders = async () => {
    try {
      // If admin, fetching orders gives all; for normal users, we filter by customer email or fetch available orders
      const { data } = await api.get('/orders').catch(() => ({ data: [] }));
      const allOrders = Array.isArray(data) ? data : data.orders || [];
      const userOrders = allOrders.filter(
        (o) => o.customer && o.customer.email.toLowerCase() === user?.email?.toLowerCase()
      );
      setOrders(userOrders.length > 0 ? userOrders : allOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      shipped: 'bg-purple-100 text-purple-800 border-purple-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };
    return (
      <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </span>
    );
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
          <p className="text-gray-500 text-sm mt-1">Track and manage your order history</p>
        </div>
        <Link to="/products" className="text-primary hover:underline font-medium text-sm">
          Browse Products →
        </Link>
      </div>

      {!user ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <p className="text-gray-600 mb-4">Please sign in to view your order history.</p>
          <Link to="/login" className="btn-primary">
            Sign In Now
          </Link>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="text-4xl mb-3">📦</div>
          <h3 className="text-lg font-bold text-gray-800">No orders found</h3>
          <p className="text-gray-500 mt-1 mb-6 text-sm">You haven't placed any orders yet.</p>
          <Link to="/products" className="btn-primary">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50/80 px-6 py-4 border-b flex flex-wrap justify-between items-center gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Order ID:</span>{' '}
                  <span className="font-bold text-gray-900">#{order._id.slice(-8)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Placed on:</span>{' '}
                  <span className="font-medium text-gray-700">{new Date(order.createdAt).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total:</span>{' '}
                  <span className="font-bold text-primary text-base">${order.total?.toFixed(2)}</span>
                </div>
                <div>{getStatusBadge(order.status)}</div>
              </div>

              <div className="p-6">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Ordered Items</h4>
                <div className="divide-y divide-gray-100">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="py-3 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-500">
                          {item.quantity}x
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{item.name || item.product?.name || 'Gadget Item'}</p>
                          <p className="text-xs text-gray-500">${item.price?.toFixed(2)} each</p>
                        </div>
                      </div>
                      <div className="font-bold text-sm text-gray-800">
                        ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500 flex flex-wrap justify-between gap-2">
                  <span>Shipping Address: {order.customer?.address}, {order.customer?.city}</span>
                  <span>Contact: {order.customer?.phone}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyOrders;
